
import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { Tables } from "@/integrations/supabase/types"
import { useAuth } from "./useAuth"
import { errorHandler } from "@/utils/errorHandler"
import { logger } from "@/utils/logger"

type Conversation = Tables<"conversations">
type Message = Tables<"messages">
type ConversationParticipant = Tables<"conversation_participants">
type Profile = Tables<"profiles">

type ConversationWithDetails = Conversation & {
  participants: (ConversationParticipant & { profiles: Profile })[]
  last_message: (Message & { sender?: { id: string; full_name: string } }) | null
  unread_count: number
}

export const useConversations = () => {
  const { profile } = useAuth()
  
  return useQuery({
    queryKey: ["conversations", profile?.id],
    queryFn: async () => {
      if (!profile?.id) {
        logger.info("Usuário não autenticado", "useConversations")
        return []
      }

      try {
        logger.info("Carregando conversas", "useConversations", { 
          userId: profile.id,
          userRole: profile.role 
        })

        // Buscar conversas onde o usuário participa
        const { data: conversations, error: conversationsError } = await supabase
          .from("conversations")
          .select(`
            *,
            participants:conversation_participants(
              *,
              profiles:profiles(*)
            ),
            messages:messages(
              *,
              sender:profiles!messages_sender_id_fkey(
                id,
                full_name
              )
            )
          `)
          .eq("is_active", true)
          .order("updated_at", { ascending: false })

        if (conversationsError) {
          logger.error("Erro ao buscar conversas", "useConversations", conversationsError)
          return []
        }

        if (!conversations || conversations.length === 0) {
          logger.info("Nenhuma conversa encontrada", "useConversations")
          return []
        }

        // Filtrar conversas onde o usuário participa
        const userConversations = conversations.filter(conv => {
          const userParticipation = conv.participants?.find(p => p.profiles?.id === profile.id)
          return !!userParticipation
        })

        // Processar detalhes de cada conversa
        const conversationsWithDetails = userConversations.map(conv => {
          const lastMessage = conv.messages && conv.messages.length > 0 
            ? conv.messages.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
            : null

          return {
            ...conv,
            participants: conv.participants || [],
            last_message: lastMessage,
            unread_count: 0
          }
        })

        logger.info("Conversas processadas com sucesso", "useConversations", {
          total: conversations.length,
          filtered: conversationsWithDetails.length,
          userRole: profile.role
        })
        
        return conversationsWithDetails as ConversationWithDetails[]
      } catch (error) {
        logger.error("Erro na query de conversas", "useConversations", error)
        return []
      }
    },
    enabled: !!profile?.id,
    retry: 1,
    retryDelay: 1000
  })
}

// Re-exportar os hooks de outros arquivos para manter compatibilidade
export { useMessages, useSendMessage } from "./useMessages"
export { useCreateBroadcastMessage } from "./useBroadcast"
export { useCreateDirectConversation } from "./useDirectConversation"
export { useAvailableUsers } from "./useAvailableUsers"
